import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import {
	Badge,
	Box,
	Button,
	ButtonGroup,
	Grid2,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Moment } from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ATHENS_GRAY } from "@/shared/colors";
import type { WorkOrder } from "@/shared/models/WorkOrder";
import {
	getWorkOrders,
	getWorkOrderPendingCounts,
	reorderWorkOrders,
	type PendingCount,
} from "@/shared/repositories/WorkOrderRepository";
import { useLineShiftNavigator } from "./hooks/useLineShiftNavigator";
import { LineShiftNavigator } from "./components/LineShiftNavigator";
import { AssetButtonGroup } from "@/shared/selectors/AssetButtonGroup";
import { invalidatePlanQueries } from "@/shared/utils/planCache";
import { WorkOrderFilters } from "./components/WorkOrderFilters";
import { WorkOrdersTable } from "./components/WorkOrdersTable";
import { useAreaIds } from "./hooks/useAreaIds";
import { useAssetNavigation } from "./hooks/useAssetNavigation";
import { useWorkOrderFilters } from "./hooks/useWorkOrderFilters";

const moment = getMomentTz();

type ViewMode = "shift" | "period";
const VIEW_STORAGE_KEY = "workOrderView";

type StoredView = {
	viewMode: ViewMode;
};

const loadStoredView = (): StoredView | null => {
	try {
		const raw = localStorage.getItem(VIEW_STORAGE_KEY);
		return raw ? (JSON.parse(raw) as StoredView) : null;
	} catch {
		return null;
	}
};

const toNumberParam = (value: string | null) => {
	if (!value) return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

export const WorkOrdersPage = () => {
	const [error, setError] = useState("");
	const [notice, setNotice] = useState<{
		message: string;
		severity: "info" | "success";
	} | null>(null);
	const queryClient = useQueryClient();

	// The URL mirrors the shift being navigated and the selected row, so any view
	// can be shared or reloaded. Arriving with those params (gantt "See in
	// Manager") replays them once: the deep-link intent is read at mount only,
	// after which the URL becomes a projection of the page state.
	const [searchParams, setSearchParams] = useSearchParams();
	const deepLinkShiftIdRef = useRef(toNumberParam(searchParams.get("shiftId")));
	const deepLinkWorkOrderIdRef = useRef(
		toNumberParam(searchParams.get("workOrderId")),
	);
	const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<number | null>(
		() => deepLinkWorkOrderIdRef.current,
	);

	// Shift vs Period navigation. Shift mode keeps the asset-aware navigator
	// (Welds 8h / Plant 12h); Period mode searches a free production-date range.
	const [viewMode, setViewMode] = useState<ViewMode>(() =>
		deepLinkShiftIdRef.current
			? "shift"
			: (loadStoredView()?.viewMode ?? "shift"),
	);
	const [periodStart, setPeriodStart] = useState<Moment | null>(() =>
		moment().subtract(6, "days").startOf("day"),
	);
	const [periodEnd, setPeriodEnd] = useState<Moment | null>(() =>
		moment().endOf("day"),
	);
	const isPeriod = viewMode === "period";
	const periodStartStr = periodStart ? periodStart.format("YYYY-MM-DD") : null;
	const periodEndStr = periodEnd ? periodEnd.format("YYYY-MM-DD") : null;

	useEffect(() => {
		try {
			localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify({ viewMode }));
		} catch {
			// ignore storage write failures
		}
	}, [viewMode]);

	const { areaIds, defaultAssetId } = useAreaIds();
	const { assetId, assetName, parentAssetId, handleAssetChange, delayAssets } =
		useAssetNavigation(areaIds, defaultAssetId);

	const {
		shiftInfo,
		loading: navigatorLoading,
		getShiftDataFromAPI,
	} = useLineShiftNavigator(assetId, setError);
	const deepLinkAppliedRef = useRef(false);
	const assetsReady = !!delayAssets;

	// Resolve the requested line/date before loading shifts. Permanent owners can
	// inherit individual days, so there is no continuous single-owner navigator.
	// Preserve the viewed production date when changing lines; deep links win once.
	// biome-ignore lint/correctness/useExhaustiveDependencies: navigation mutates shiftInfo
	useEffect(() => {
		if (!assetId || !assetsReady) return;
		const deepLink = !deepLinkAppliedRef.current
			? deepLinkShiftIdRef.current
			: null;
		void getShiftDataFromAPI(
			deepLink ? null : (shiftInfo?.CurrentProductionDate ?? null),
			deepLink ?? 0,
			assetId,
		);
	}, [assetId, assetsReady]);
	useEffect(() => {
		if (shiftInfo) deepLinkAppliedRef.current = true;
	}, [shiftInfo]);

	const shiftId = shiftInfo?.CurrentShiftId ?? null;

	// Selection belongs to the line and shift we left; keep a deep link on first load.
	const previousView = useRef({ assetId, shiftId });
	useEffect(() => {
		const previous = previousView.current;
		if (
			previous.shiftId != null &&
			(previous.assetId !== assetId || previous.shiftId !== shiftId)
		) {
			setSelectedWorkOrderId(null);
		}
		previousView.current = { assetId, shiftId };
	}, [assetId, shiftId]);

	// Project shift + selected row onto the URL (replace, so the back button still
	// walks pages instead of every shift step). Period mode has no shareable
	// shift, so it keeps the URL clean.
	useEffect(() => {
		if (!deepLinkAppliedRef.current) return;
		const next = new URLSearchParams();
		if (!isPeriod && shiftId) {
			next.set("shiftId", String(shiftId));
			if (selectedWorkOrderId != null) {
				next.set("workOrderId", String(selectedWorkOrderId));
			}
		}
		if (next.toString() !== searchParams.toString()) {
			setSearchParams(next, { replace: true });
		}
	}, [shiftId, isPeriod, selectedWorkOrderId, searchParams, setSearchParams]);

	// When on the current shift (NOW), pass null so the SP resolves each asset's
	// active shift independently (bridges 8h Welds and 12h Plant schemes).
	// Compare against actual clock time instead of NextShiftId, which can be
	// null for any last-of-day shift even when not currently active.
	const isCurrentShift = shiftInfo
		? new Date(shiftInfo.CurrentStart) <= new Date() &&
			new Date() <= new Date(shiftInfo.CurrentEnd)
		: false;
	const isCurrentOrFutureShift = shiftInfo
		? new Date(shiftInfo.CurrentEnd) >= new Date()
		: false;

	const { data: workOrders, isLoading } = useQuery<WorkOrder[], Error>({
		queryKey: [
			"workOrders",
			assetId,
			isPeriod ? "period" : shiftId,
			isPeriod ? periodStartStr : null,
			isPeriod ? periodEndStr : null,
		],
		queryFn: () =>
			isPeriod
				? getWorkOrders(
						assetId,
						null,
						periodStart ? periodStart.toDate() : null,
						periodEnd ? periodEnd.toDate() : null,
					)
				: getWorkOrders(assetId, shiftId),
		enabled: isPeriod ? !!(periodStart && periodEnd) : true,
		onError: (e: Error) => setError(e.message),
		refetchInterval: (data) => {
			if (isPeriod) return false;
			const hasLiveWorkOrder = data?.some((wo) =>
				["Loaded", "Running"].includes(wo.Status),
			);
			if (hasLiveWorkOrder || isCurrentOrFutureShift) return 10000;
			return 30000;
		},
		refetchIntervalInBackground: false,
		staleTime: 5000,
		// Real-time sync: when another browser mutates work orders, SignalR
		// invalidates every query subscribed to the read SP.
		meta: { realtime: ["FAB.GetWorkOrders"] },
	});

	const pendingCountShiftId = isCurrentShift ? null : shiftId;

	const { data: pendingCountsRaw } = useQuery<PendingCount[], Error>({
		queryKey: ["workOrderPendingCounts", pendingCountShiftId],
		queryFn: () => getWorkOrderPendingCounts(pendingCountShiftId),
		onError: (e: Error) => setError(e.message),
		refetchInterval: isCurrentOrFutureShift ? 30000 : false,
		refetchIntervalInBackground: false,
		meta: { realtime: ["FAB.GetWorkOrderPendingCounts"] },
	});

	const pendingCountsMap = pendingCountsRaw
		? Object.fromEntries(
				pendingCountsRaw.map((r) => [r.AssetId, r.PendingCount]),
			)
		: undefined;

	const {
		filters,
		setFilters,
		clearFilters,
		filtersVersion,
		filteredRows,
		activeFilterCount,
		showFilters,
		setShowFilters,
		customers,
		sections,
	} = useWorkOrderFilters(workOrders);

	// A deep-linked work order always belongs to the shift we travelled with, but
	// the persisted client-side filters can still hide it. Clear them once. Only
	// on arrival: a row picked by hand is visible by definition, so a later
	// filter change must never be undone here.
	const filterRescueDoneRef = useRef(false);

	useEffect(() => {
		const target = deepLinkWorkOrderIdRef.current;
		if (!target || filterRescueDoneRef.current) return;
		if (!deepLinkAppliedRef.current || isLoading || !workOrders) return;
		const inData = workOrders.some((w) => w.WorkOrderId === target);
		const inView = filteredRows.some((w) => w.WorkOrderId === target);
		if (!inData) return;
		filterRescueDoneRef.current = true;
		if (!inView) {
			clearFilters();
			const number = workOrders.find(
				(wo) => wo.WorkOrderId === target,
			)?.WorkOrderNumber;
			setNotice({
				message: number
					? `Filters cleared to show work order ${number}`
					: "Filters cleared to show the work order",
				severity: "info",
			});
		}
	}, [isLoading, workOrders, filteredRows, clearFilters]);

	const handleReorder = useCallback(
		async (orderedIds: number[]) => {
			try {
				const resp = await reorderWorkOrders(assetId, orderedIds);
				if (!resp.ok) {
					setError(resp.message || "Error reordering work orders");
				} else {
					// The reorder repacks plan windows, so the calendar and the undo
					// entry are stale too
					invalidatePlanQueries(queryClient, {
						assetIds: [assetId],
						workOrders: true,
						planChange: true,
					});
				}
			} catch (e: any) {
				setError(e.message);
			}
		},
		[assetId, queryClient],
	);

	const handleNavigateToShift = useCallback(
		async (targetShiftId: number) => {
			if (targetShiftId !== shiftId) {
				await getShiftDataFromAPI(null, targetShiftId, assetId);
			}
		},
		[shiftId, getShiftDataFromAPI, assetId],
	);

	const handlePrint = useCallback(() => {
		if (!shiftId || !assetName) return;
		const url = `/api/work-order-sheet/${shiftId}/${assetName}`;
		window.open(url, "_blank", "noopener,noreferrer");
	}, [shiftId, assetName]);

	// The sheet only prints Pending work orders, and ignores the table filters
	const hasPrintableWorkOrders =
		workOrders?.some((wo) => wo.Status === "Pending") ?? false;
	const canPrint =
		!isPeriod && !!shiftInfo && !!assetName && hasPrintableWorkOrders;
	const printTitle = canPrint
		? ""
		: isPeriod
			? "Printing is only available in Shift view"
			: "No pending work orders to print";

	return (
		<>
			<HelmetDexteel title="Work Orders" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				style={{ backgroundColor: ATHENS_GRAY }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Work Orders
					</Typography>
				</Grid2>

				<Grid2 size="auto">
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<Box display="flex" alignItems="center" gap={2}>
							<ButtonGroup size="small" color="primary">
								<Button
									variant={viewMode === "shift" ? "contained" : "outlined"}
									onClick={() => setViewMode("shift")}
									sx={{ textTransform: "none", px: 2 }}
								>
									Shift
								</Button>
								<Button
									variant={viewMode === "period" ? "contained" : "outlined"}
									onClick={() => setViewMode("period")}
									sx={{ textTransform: "none", px: 2 }}
								>
									Period
								</Button>
							</ButtonGroup>
							{isPeriod ? (
								<Box display="flex" alignItems="center" gap={1}>
									<DatePicker
										label="Start"
										value={periodStart}
										onChange={(v) =>
											setPeriodStart(v ? v.startOf("day") : null)
										}
										format="MM/DD/YYYY"
										maxDate={periodEnd ?? undefined}
										slotProps={{
											textField: { size: "small", sx: { width: 150 } },
										}}
									/>
									<DatePicker
										label="End"
										value={periodEnd}
										onChange={(v) => setPeriodEnd(v ? v.endOf("day") : null)}
										format="MM/DD/YYYY"
										minDate={periodStart ?? undefined}
										slotProps={{
											textField: { size: "small", sx: { width: 150 } },
										}}
									/>
								</Box>
							) : (
								<LineShiftNavigator
									shiftInfo={shiftInfo}
									loading={navigatorLoading}
									onNavigate={getShiftDataFromAPI}
								/>
							)}
						</Box>
					</Paper>
				</Grid2>
				<Grid2 size="grow">
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<Box
							display="flex"
							justifyContent="space-between"
							alignItems="center"
							width="100%"
						>
							<AssetButtonGroup
								value={assetName ?? null}
								onChange={(slug) => handleAssetChange(slug)}
								pendingCounts={pendingCountsMap}
								areaIds={areaIds}
							/>
							<Box display="flex" alignItems="center" gap={1}>
								<Badge
									badgeContent={activeFilterCount}
									color="error"
									invisible={showFilters || activeFilterCount === 0}
								>
									<Button
										variant="outlined"
										color={showFilters ? "inherit" : "primary"}
										onClick={() => setShowFilters((prev) => !prev)}
										sx={{ minWidth: 0, px: 1.5 }}
									>
										<FilterListIcon />
									</Button>
								</Badge>
								<Tooltip title={printTitle}>
									{/* a disabled button fires no pointer events */}
									<span>
										<Button
											variant="contained"
											startIcon={<PrintIcon />}
											onClick={handlePrint}
											disabled={!canPrint}
											sx={{ textTransform: "none" }}
										>
											Print
										</Button>
									</span>
								</Tooltip>
							</Box>
						</Box>
					</Paper>
				</Grid2>

				{showFilters && (
					<Grid2 size={{ xs: 12 }}>
						<WorkOrderFilters
							key={filtersVersion}
							customers={customers}
							sections={sections}
							onFilterChange={setFilters}
						/>
					</Grid2>
				)}

				<Grid2 size={{ md: 12, xs: 12 }}>
					<WorkOrdersTable
						onWorkOrderSaved={(workOrderNumber) =>
							setNotice({
								message: `Work order ${workOrderNumber} saved successfully`,
								severity: "success",
							})
						}
						isLoading={isLoading}
						rows={filteredRows}
						assetId={assetId}
						parentAssetId={parentAssetId}
						shiftStart={shiftInfo?.CurrentStart ?? null}
						shiftEnd={shiftInfo?.CurrentEnd ?? null}
						areaIds={areaIds}
						isCurrentShift={isCurrentShift}
						isPeriod={isPeriod}
						scrollToWorkOrderId={deepLinkWorkOrderIdRef.current}
						onSelectedWorkOrderChange={setSelectedWorkOrderId}
						onReorder={handleReorder}
						onReorderCancel={() =>
							queryClient.invalidateQueries({
								queryKey: ["workOrders", assetId],
							})
						}
						onNavigateToShift={handleNavigateToShift}
					/>
				</Grid2>
			</Grid2>
			<NotificationSnackBar
				message={notice?.message ?? null}
				severity={notice?.severity ?? "info"}
				onHide={() => setNotice(null)}
			/>
			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>
		</>
	);
};
